import React, { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import styled from 'styled-components'

import { convertDataForSortableList, getNodes } from 'graphql/utils'
import { SortableList, SortableItem, TopToolbar } from 'components/lists'
import {
    CinemaFragment,
    GetVideosQuery,
    GetVideosQueryVariables
} from 'generated/graphql'
import { useQuery, useEventSlug, useSlug } from 'hooks'
import { _ } from 'utils'
import { addModal } from 'features/modals/modalSlice'
import { videosQuery } from '../graphql/query'
import CreateMovie from '../components/CreateMovie'
import { updateMovieOrder } from '../graphql/mutation'
import MovieRow from '../components/MovieRow'
import { handleSubmit, importProps } from '../importConfig'

const S = {
    Wrapper: styled.div`
        width: 100%;
    `,
    TopToolbar: styled.div`
        margin: -20px 10px 20px 10px;
    `
}

interface Props {
    onGetCinema: (cinema: CinemaFragment) => void
}

const Movies = ({ onGetCinema }: Props) => {
    const dispatch = useDispatch()
    const eventSlug = useEventSlug()
    const cinemaSlug = useSlug('cinemaSlug')
    const [search, setSearch] = useState('')
    const [videos, setVideos] = useState<SortableItem[]>([])
    const [maxOrder, setMaxOrder] = useState(1e6)
    const [cinema, setCinema] = useState(null)

    const { data: cinemaData, fetchData, requestStatus } = useQuery<
        GetVideosQuery, GetVideosQueryVariables
    >(videosQuery, { eventId: eventSlug, first: 99, search })

    useEffect(() => {
        if (cinemaData) {
            const _cinema = getNodes(cinemaData?.event?.cinemas)
                .find((e) => e.parent.slug === cinemaSlug)
            onGetCinema(_cinema)
            setCinema(_cinema)
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cinemaSlug, cinemaData])

    const showCreateModal = () => {
        dispatch(addModal({
            Component: ({ close }: { close: () => void }) => (
                <CreateMovie
                    close={close}
                    callGetMovies={fetchData}
                    maxOrder={maxOrder}
                    cinemaId={cinema?.id}
                />
            ),
            header: _('cinema.create_movie.modal_header'),
        }))
    }

    const renderItem = (video) => (
        <MovieRow movie={video} callGetMovies={fetchData} />
    )

    useEffect(() => {
        if (cinema) {
            setVideos(
                convertDataForSortableList(
                    getNodes(cinema?.movies),
                    renderItem
                )
            )
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cinema])

    return (
        <S.Wrapper>
            <S.TopToolbar>
                <TopToolbar
                    addItemText={_('movie.add_button')}
                    onAddItem={showCreateModal}
                    onSearch={setSearch}
                    searchPlaceHolder={_('movie.search_placeholder')}
                    bulkActions={{
                        importProps: {
                            ...importProps,
                            onSubmit: (data) => handleSubmit(cinema?.id, data, dispatch, fetchData)
                        }
                    }}
                />
            </S.TopToolbar>
            <SortableList
                initialList={videos}
                updateOrder={updateMovieOrder}
                disableSort={!!search}
                onUpdateMaxOrder={setMaxOrder}
                requestStatus={requestStatus}
            />
        </S.Wrapper>
    )
}

export default Movies
